import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCompanyId, setActiveCompanyId] = useState(
    () => searchParams.get("company") || localStorage.getItem("active_company_id") || null
  );

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      // If URL has ?company=, prefer it
      const urlCompany = searchParams.get("company");
      if (urlCompany && data.find((c) => c.id === urlCompany)) {
        setActiveCompanyId(urlCompany);
        localStorage.setItem("active_company_id", urlCompany);
      } else if (data.length && !data.find((c) => c.id === activeCompanyId)) {
        setActiveCompanyId(data[0].id);
        localStorage.setItem("active_company_id", data[0].id);
      }
    } catch (e) {
      console.warn("Failed to load companies", e);
    }
  }, [activeCompanyId, searchParams]);

  useEffect(() => {
    if (user && user !== false) loadCompanies();
  }, [user, loadCompanies]);

  const setCompany = useCallback((id) => {
    setActiveCompanyId(id);
    localStorage.setItem("active_company_id", id);
    if (searchParams.get("company")) {
      searchParams.delete("company");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId) || null;

  const value = useMemo(
    () => ({ companies, activeCompanyId, activeCompany, setCompany, reloadCompanies: loadCompanies }),
    [companies, activeCompanyId, activeCompany, setCompany, loadCompanies]
  );

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  return useContext(CompanyContext);
}
